import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { permissionsApi } from '@/lib/pillarApi';
import { useAuthStore } from '@/store/auth';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import type {
    GrantPolicyPermissionBundleRequest,
    ListPolicyPermissionGrantsResponse,
    PermissionsBundlePublic,
    PolicyPermissionBundlesSearchPageResponse,
} from '@pillar-portal/api-types';
import type { Role } from '@pillar-portal/database/dist/frontend';

interface PolicyGrantAssignableRoleAssignment {
    role_assignment_uuid: string;
    role: Role;
    organization_uuid: string | null;
    organization_name: string | null;
}

interface PolicyPermissionGrantsCardProps {
    roleAssignments: ReadonlyArray<PolicyGrantAssignableRoleAssignment>;
    disabled?: boolean;
}

function formatRoleAssignmentLabel(
    roleAssignment: PolicyGrantAssignableRoleAssignment
): string {
    if (roleAssignment.organization_uuid === null) {
        return `${roleAssignment.role} (internal)`;
    }
    const orgName: string = roleAssignment.organization_name ?? 'Organization';
    return `${roleAssignment.role} @ ${orgName}`;
}

function getRequiredRoleAssignmentUuid(
    roleAssignment: PolicyGrantAssignableRoleAssignment
): string {
    if (roleAssignment.role_assignment_uuid === undefined) {
        throw new Error('Missing role assignment uuid');
    }
    return roleAssignment.role_assignment_uuid;
}

function getRequiredOrganizationUuid(
    roleAssignment: PolicyGrantAssignableRoleAssignment
): string {
    if (roleAssignment.organization_uuid === undefined || roleAssignment.organization_uuid === null) {
        throw new Error('Missing organization uuid');
    }
    return roleAssignment.organization_uuid;
}

export function PolicyPermissionGrantsCard(props: PolicyPermissionGrantsCardProps) {
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const policyGrantsVersion: number = useDataInvalidationStore(
        (state) => state.policyGrantsVersion
    );
    const bumpPolicyGrantsVersion = useDataInvalidationStore(
        (state) => state.bumpPolicyGrantsVersion
    );

    const orgScopedRoleAssignments: ReadonlyArray<PolicyGrantAssignableRoleAssignment> =
        useMemo(() => {
        return props.roleAssignments.filter(
            (roleAssignment: PolicyGrantAssignableRoleAssignment) =>
                roleAssignment.organization_uuid !== null
        );
    }, [props.roleAssignments]);

    const [selectedRoleAssignmentUuid, setSelectedRoleAssignmentUuid] = useState<string | null>(
        () => {
            const first: PolicyGrantAssignableRoleAssignment | undefined =
                orgScopedRoleAssignments[0];
            return first ? getRequiredRoleAssignmentUuid(first) : null;
        }
    );

    useEffect(() => {
        if (selectedRoleAssignmentUuid !== null) {
            return;
        }
        const first: PolicyGrantAssignableRoleAssignment | undefined =
            orgScopedRoleAssignments[0];
        if (!first) {
            return;
        }
        setSelectedRoleAssignmentUuid(getRequiredRoleAssignmentUuid(first));
    }, [orgScopedRoleAssignments, selectedRoleAssignmentUuid]);

    const selectedRoleAssignment: PolicyGrantAssignableRoleAssignment | null =
        useMemo(() => {
        if (selectedRoleAssignmentUuid === null) {
            return null;
        }
        const found: PolicyGrantAssignableRoleAssignment | undefined =
            orgScopedRoleAssignments.find(
            (roleAssignment: PolicyGrantAssignableRoleAssignment) =>
                roleAssignment.role_assignment_uuid === selectedRoleAssignmentUuid
        );
        return found ?? null;
    }, [orgScopedRoleAssignments, selectedRoleAssignmentUuid]);

    const selectedRoleAssignmentUuidRequired: string | null =
        selectedRoleAssignment === null ? null : getRequiredRoleAssignmentUuid(selectedRoleAssignment);
    const selectedOrganizationUuid: string | null =
        selectedRoleAssignment === null ? null : getRequiredOrganizationUuid(selectedRoleAssignment);

    const { data: grantsData, isLoading: isGrantsLoading, error: grantsError } =
        useQuery<ListPolicyPermissionGrantsResponse>({
            queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                'policyPermissionGrants',
                selectedRoleAssignmentUuid ?? 'none',
                policyGrantsVersion,
            ]),
            queryFn: async () => {
                if (selectedRoleAssignmentUuid === null) {
                    throw new Error('Missing role assignment uuid');
                }
                return await permissionsApi.listPolicyPermissionGrantsForRoleAssignment(
                    selectedRoleAssignmentUuid
                );
            },
            enabled: selectedRoleAssignmentUuid !== null,
            staleTime: 0,
        });

    const grantedBundles: ReadonlyArray<PermissionsBundlePublic> = grantsData?.bundles ?? [];

    const [selectedPolicyBundleUuid, setSelectedPolicyBundleUuid] = useState<string | null>(null);
    const [pendingRevokeBundleUuid, setPendingRevokeBundleUuid] = useState<string | null>(null);
    const [isRevokeConfirmOpen, setIsRevokeConfirmOpen] = useState<boolean>(false);

    useEffect(() => {
        setSelectedPolicyBundleUuid(null);
    }, [selectedRoleAssignmentUuid]);

    const grantMutation = useMutation({
        mutationFn: async () => {
            if (selectedRoleAssignmentUuidRequired === null) {
                throw new Error('Missing role assignment uuid');
            }
            if (selectedPolicyBundleUuid === null) {
                throw new Error('Select a policy bundle to grant');
            }
            const payload: GrantPolicyPermissionBundleRequest = {
                permissions_bundle_uuid: selectedPolicyBundleUuid,
            };
            return await permissionsApi.grantPolicyPermissionBundleToRoleAssignment(
                selectedRoleAssignmentUuidRequired,
                payload
            );
        },
        onSuccess: () => {
            bumpPolicyGrantsVersion();
            setSelectedPolicyBundleUuid(null);
        },
        onError: (err) => {
            console.error('Error granting policy bundle:', err);
        },
    });

    const revokeMutation = useMutation({
        mutationFn: async (bundleUuid: string) => {
            if (selectedRoleAssignmentUuidRequired === null) {
                throw new Error('Missing role assignment uuid');
            }
            return await permissionsApi.revokePolicyPermissionBundleFromRoleAssignment(
                selectedRoleAssignmentUuidRequired,
                bundleUuid
            );
        },
        onSuccess: () => {
            bumpPolicyGrantsVersion();
        },
        onError: (err) => {
            console.error('Error revoking policy bundle:', err);
        },
    });

    return (
        <>
            <Card className="mb-6">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle>Permissions Policy Grants</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                            Grant and revoke policy bundles per role assignment.
                        </p>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    {orgScopedRoleAssignments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            This user has no organization-scoped role assignments.
                        </p>
                    ) : (
                        <>
                            <div className="space-y-2">
                                <div className="text-sm font-medium">Role assignment</div>
                                <select
                                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={selectedRoleAssignmentUuid ?? ''}
                                    disabled={props.disabled === true}
                                    onChange={(e) => {
                                        const nextUuid = e.target.value.trim();
                                        setSelectedRoleAssignmentUuid(
                                            nextUuid.length > 0 ? nextUuid : null
                                        );
                                    }}
                                >
                                    {orgScopedRoleAssignments.map(
                                        (
                                            roleAssignment: PolicyGrantAssignableRoleAssignment
                                        ) => {
                                            const roleAssignmentUuid: string =
                                                getRequiredRoleAssignmentUuid(roleAssignment);
                                            return (
                                                <option
                                                    key={roleAssignmentUuid}
                                                    value={roleAssignmentUuid}
                                                >
                                                    {formatRoleAssignmentLabel(roleAssignment)}
                                                </option>
                                            );
                                        }
                                    )}
                                </select>
                            </div>

                            <div className="flex items-end gap-4">
                                <div className="flex-1">
                                    <OmnisearchSelect<PermissionsBundlePublic, string>
                                        label="+ Policy"
                                        placeholder="Search policy bundles..."
                                        pageSize={25}
                                        disabled={
                                            props.disabled === true ||
                                            selectedOrganizationUuid === null ||
                                            selectedRoleAssignmentUuid === null
                                        }
                                        resetToken={`org=${selectedOrganizationUuid ?? 'none'}`}
                                        fetchPage={async (params) => {
                                            if (selectedOrganizationUuid === null) {
                                                throw new Error('Missing organization uuid');
                                            }
                                            const response: PolicyPermissionBundlesSearchPageResponse =
                                                await permissionsApi.searchPolicyPermissionBundlesForOrganization(
                                                    selectedOrganizationUuid,
                                                    {
                                                        ...(params.terms !== undefined
                                                            ? { terms: [...params.terms] }
                                                            : {}),
                                                        limit: params.limit,
                                                        cursor: params.cursor,
                                                    }
                                                );
                                            return {
                                                items: response.bundles,
                                                pagination: response.pagination,
                                            };
                                        }}
                                        getItemId={(bundle) => bundle.permissions_bundle_uuid}
                                        getItemLabel={(bundle) => bundle.name}
                                        getItemFilterText={(bundle) =>
                                            `${bundle.name} ${bundle.description ?? ''} ${bundle.permissions_bundle_uuid}`
                                                .trim()
                                                .toLowerCase()
                                        }
                                        selectedItemId={selectedPolicyBundleUuid}
                                        onClearSelection={() => {
                                            setSelectedPolicyBundleUuid(null);
                                        }}
                                        onSelect={(bundle) =>
                                            setSelectedPolicyBundleUuid(
                                                bundle.permissions_bundle_uuid
                                            )
                                        }
                                    />
                                </div>
                                <Button
                                    onClick={() => grantMutation.mutate()}
                                    disabled={
                                        props.disabled === true ||
                                        selectedRoleAssignmentUuid === null ||
                                        selectedPolicyBundleUuid === null ||
                                        grantMutation.isPending
                                    }
                                >
                                    {grantMutation.isPending ? 'Saving...' : 'Save'}
                                </Button>
                            </div>

                            {grantMutation.error instanceof Error && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {grantMutation.error.message}
                                    </AlertDescription>
                                </Alert>
                            )}

                            <div className="space-y-3">
                                <div className="text-sm font-medium">Granted policies</div>

                                {isGrantsLoading ? (
                                    <p className="text-sm text-muted-foreground">Loading...</p>
                                ) : grantsError instanceof Error ? (
                                    <Alert variant="destructive">
                                        <AlertDescription>{grantsError.message}</AlertDescription>
                                    </Alert>
                                ) : grantedBundles.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">
                                        No policy bundles granted.
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {grantedBundles.map((bundle: PermissionsBundlePublic) => (
                                            <div
                                                key={bundle.permissions_bundle_uuid}
                                                className="flex items-center justify-between border rounded-md p-3"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="font-medium">
                                                        {bundle.name}
                                                    </div>
                                                    <Badge variant="secondary">
                                                        {bundle.permissions.length} permissions
                                                    </Badge>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    disabled={
                                                        props.disabled === true ||
                                                        revokeMutation.isPending
                                                    }
                                                    onClick={() => {
                                                        setPendingRevokeBundleUuid(
                                                            bundle.permissions_bundle_uuid
                                                        );
                                                        setIsRevokeConfirmOpen(true);
                                                    }}
                                                >
                                                    -
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            <ConfirmationModal
                open={isRevokeConfirmOpen}
                onOpenChange={setIsRevokeConfirmOpen}
                message="Revoke this policy bundle?"
                confirmLabel="Revoke"
                isLoading={revokeMutation.isPending}
                onConfirm={() => {
                    if (pendingRevokeBundleUuid === null) {
                        return;
                    }
                    setIsRevokeConfirmOpen(false);
                    revokeMutation.mutate(pendingRevokeBundleUuid);
                    setPendingRevokeBundleUuid(null);
                }}
                onCancel={() => {
                    setPendingRevokeBundleUuid(null);
                    setIsRevokeConfirmOpen(false);
                }}
            />
        </>
    );
}
